import { useMemo, useState } from 'react'
import { Plus, Users, X } from 'lucide-react'
import { Callout, EmptyState, ErrorState, FormDialog, PermissionGate } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useDepartments, useGrades, useLegalEntities, useLocations } from '@/features/org'
import { toApiProblem } from '@/lib/api/client'
import {
  useGeofenceAssignments, useGeofenceCoverage, useGeofenceMutations, useGeofencePolicies,
} from '../../api/geofence'
import { GEOFENCE_SCOPES } from '../../types/geofence'
import type { GeofenceScope } from '../../types/geofence'

const ORDER: GeofenceScope[] = ['EMPLOYEE', 'DEPARTMENT', 'LOCATION', 'GRADE', 'LEGAL_ENTITY']

/** The things a policy can be put on, for one scope type, as id + label. */
function useScopeOptions(scope: GeofenceScope): { id: string; label: string }[] | undefined {
  // The coverage list rather than the directory: it holds every current employee, where the
  // directory pages at 200, and it shows the policy each person is on now.
  const employees = useGeofenceCoverage(scope === 'EMPLOYEE')
  const departments = useDepartments()
  const locations = useLocations()
  const grades = useGrades()
  const entities = useLegalEntities()
  switch (scope) {
    case 'EMPLOYEE':
      return employees.data?.rows.map((e) => ({
        id: e.employeeId,
        label: `${e.employeeName ?? ''} (${e.employeeCode})${e.departmentName ? ` · ${e.departmentName}` : ''}`
          + ` — now: ${e.policyName ?? 'no policy'}`,
      }))
    case 'DEPARTMENT': return departments.data?.map((d) => ({ id: d.id, label: d.name }))
    case 'LOCATION': return locations.data?.map((l) => ({ id: l.id, label: l.name }))
    case 'GRADE': return grades.data?.map((g) => ({ id: g.id, label: g.name }))
    case 'LEGAL_ENTITY': return entities.data?.map((e) => ({ id: e.id, label: e.name }))
  }
}

function AssignDialog({ onClose }: { onClose: () => void }) {
  const policies = useGeofencePolicies()
  const { assign } = useGeofenceMutations()
  const [policyId, setPolicyId] = useState('')
  const [scope, setScope] = useState<GeofenceScope>('EMPLOYEE')
  const [search, setSearch] = useState('')
  const [chosen, setChosen] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const options = useScopeOptions(scope)

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase()
    return (options ?? []).filter((option) => !term || option.label.toLowerCase().includes(term))
  }, [options, search])

  function submit() {
    setError(null)
    if (!policyId) { setError('Choose a policy.'); return }
    if (chosen.length === 0) { setError(`Choose at least one of the ${GEOFENCE_SCOPES[scope].toLowerCase()}.`); return }
    assign.mutate({ policyId, scopeType: scope, scopeIds: chosen }, {
      onSuccess: onClose,
      onError: (caught) => setError(toApiProblem(caught)?.detail ?? 'The policy could not be assigned.'),
    })
  }

  return (
    <FormDialog
      open
      onOpenChange={(open) => { if (!open) onClose() }}
      title="Assign a geofence policy"
      description="Anything already on another policy at the same level is moved to this one."
      size="lg"
      submitLabel={`Assign to ${chosen.length || ''} ${GEOFENCE_SCOPES[scope].toLowerCase()}`.replace('  ', ' ')}
      loading={assign.isPending}
      error={error}
      onSubmit={submit}
    >
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="ga-policy" required>Policy</Label>
            <Select id="ga-policy" value={policyId} placeholder="Choose a policy"
              onChange={(event) => setPolicyId(event.target.value)}>
              {policies.data?.map((policy) => (
                <option key={policy.id} value={policy.id}>{policy.name}</option>
              ))}
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="ga-scope" required>Apply to</Label>
            <Select id="ga-scope" value={scope}
              onChange={(event) => { setScope(event.target.value as GeofenceScope); setChosen([]); setSearch('') }}>
              {ORDER.map((value) => <option key={value} value={value}>{GEOFENCE_SCOPES[value]}</option>)}
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <Input aria-label="Search" placeholder="Search…" value={search}
              onChange={(event) => setSearch(event.target.value)} />
            <Button type="button" variant="ghost" size="sm"
              onClick={() => setChosen((previous) => Array.from(new Set([...previous, ...visible.map((o) => o.id)])))}>
              Select all shown
            </Button>
            {chosen.length > 0 && (
              <Button type="button" variant="ghost" size="sm" onClick={() => setChosen([])}>Clear</Button>
            )}
          </div>
          {!options && <Skeleton className="h-40" />}
          {options && (
            <div className="max-h-72 overflow-y-auto rounded-[var(--radius-control)] border border-[var(--color-border)]">
              {visible.length === 0 && <p className="p-3 text-sm text-[var(--color-muted)]">Nothing matches.</p>}
              {visible.map((option) => (
                <label key={option.id}
                  className="flex cursor-pointer items-center gap-3 border-b border-[var(--color-border)] px-3 py-2 text-sm last:border-0 hover:bg-[var(--color-page)]">
                  <input type="checkbox" className="h-4 w-4 accent-[var(--color-primary)]"
                    checked={chosen.includes(option.id)}
                    onChange={(event) => setChosen((previous) => event.target.checked
                      ? [...previous, option.id] : previous.filter((id) => id !== option.id))} />
                  {option.label}
                </label>
              ))}
            </div>
          )}
          <p className="text-xs text-[var(--color-muted)]">{chosen.length} selected</p>
        </div>
      </div>
    </FormDialog>
  )
}

/** Who each policy reaches, grouped by level in the order they win. */
export function AssignmentsTab() {
  const assignments = useGeofenceAssignments()
  const { unassign } = useGeofenceMutations()
  const [adding, setAdding] = useState(false)

  if (assignments.isPending) return <Skeleton className="h-48" />
  if (assignments.isError) return <ErrorState error={assignments.error} onRetry={() => void assignments.refetch()} />

  return (
    <div className="space-y-4">
      {adding && <AssignDialog onClose={() => setAdding(false)} />}
      <Callout title="The most specific assignment wins">
        Employee → department → work location → grade → legal entity → company default. So you can
        put &ldquo;Office staff&rdquo; on the whole entity and still exempt one driver by assigning
        &ldquo;Field staff&rdquo; to that employee.
      </Callout>
      <div className="flex justify-end">
        <PermissionGate permission="attendance:write">
          <Button type="button" onClick={() => setAdding(true)}>
            <Plus className="size-4" aria-hidden="true" />
            Assign policy
          </Button>
        </PermissionGate>
      </div>

      {assignments.data.length === 0 && (
        <EmptyState icon={Users} title="No assignments yet"
          description="Without assignments only the company default policy applies, if there is one." />
      )}

      {ORDER.map((scope) => {
        const rows = assignments.data.filter((row) => row.scopeType === scope)
        if (rows.length === 0) return null
        return (
          <Card key={scope} className="p-4">
            <h3 className="mb-3 text-sm font-semibold text-[var(--color-heading)]">
              {GEOFENCE_SCOPES[scope]} <span className="font-normal text-[var(--color-muted)]">({rows.length})</span>
            </h3>
            <ul className="divide-y divide-[var(--color-border)]">
              {rows.map((row) => (
                <li key={row.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <span className="min-w-0 truncate text-[var(--color-heading)]">{row.scopeName ?? row.scopeId}</span>
                  <span className="flex shrink-0 items-center gap-2">
                    <span className="rounded-full bg-[var(--color-primary-soft)] px-2 py-0.5 text-xs text-[var(--color-primary)]">
                      {row.policyName}
                    </span>
                    <PermissionGate permission="attendance:write">
                      <Button type="button" size="sm" variant="ghost" aria-label={`Remove ${row.scopeName ?? ''}`}
                        disabled={unassign.isPending} onClick={() => unassign.mutate(row.id)}>
                        <X className="size-4" aria-hidden="true" />
                      </Button>
                    </PermissionGate>
                  </span>
                </li>
              ))}
            </ul>
          </Card>
        )
      })}
    </div>
  )
}
